import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';

const TransferReportPrint = ({ transfers, dateRangeText, onClose }) => {
    const { t, i18n } = useTranslation();
    const { user } = useContext(AppContext);
    const contentRef = useRef(null);

    const totalQuantity = transfers.reduce((sum, t) => sum + parseFloat(t.quantity || 0), 0);

    const handlePrint = () => {
        if (!contentRef.current) return;
        const html = `<!DOCTYPE html><html><head>
            <meta charset="utf-8"><title>Stock Transfer Report — ${dateRangeText}</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 24px; }
                @page { size: A4 portrait; margin: 10mm; }
                table { border-collapse: collapse; width: 100%; }
                th { padding: 12px 8px; font-size: 11px; font-weight: 900; text-transform: uppercase; color: #4b5563; border-bottom: 2px solid #1f2937; text-align: left; }
                td { padding: 10px 8px; font-size: 11px; border-bottom: 1px solid #f3f4f6; }
                .text-right { text-align: right; } .text-orange-700 { color: #c2410c; } .text-emerald-700 { color: #047857; }
                .text-indigo-700 { color: #4338ca; } .font-black { font-weight: 900; } .font-bold { font-weight: 700; }
                .font-mono { font-family: monospace; } .text-gray-500 { color: #6b7280; } .text-gray-400 { color: #9ca3af; }
                .bg-gray-50\\/50 { background: rgba(249,250,251,0.5); } .bg-white { background: white; }
                .flex { display: flex; } .justify-between { justify-content: space-between; } .justify-end { justify-content: flex-end; }
                .items-center { align-items: center; } .items-start { align-items: flex-start; }
                .mt-4 { margin-top: 16px; } .mt-8 { margin-top: 32px; } .mt-16 { margin-top: 64px; }
                .mb-6 { margin-bottom: 24px; } .mb-8 { margin-bottom: 32px; }
                .pb-4 { padding-bottom: 16px; } .pb-6 { padding-bottom: 24px; } .pt-4 { padding-top: 16px; } .pt-8 { padding-top: 32px; }
                .border-b { border-bottom: 1px solid #e5e7eb; } .border-t-2 { border-top: 2px solid #1f2937; } .border-t { border-top: 1px solid #e5e7eb; }
                .text-gray-900 { color: #111827; } .text-sm { font-size: 12px; } .text-xs { font-size: 10px; }
                .text-3xl { font-size: 28px; } .text-lg { font-size: 16px; } .text-xl { font-size: 18px; }
                .uppercase { text-transform: uppercase; } .tracking-tight { letter-spacing: -0.025em; }
                .tracking-widest { letter-spacing: 0.1em; }
                .bg-indigo-50 { background: #eef2ff; } .text-indigo-700 { color: #4338ca; }
                .rounded { border-radius: 6px; } .inline-block { display: inline-block; }
                .px-3 { padding-left: 12px; padding-right: 12px; } .py-1 { padding-top: 4px; padding-bottom: 4px; }
                .p-3 { padding: 12px; } .w-64 { width: 256px; }
                .bg-gray-50 { background: #f9fafb; } .rounded-lg { border-radius: 8px; } .border { border: 1px solid #e5e7eb; }
                .font-semibold { font-weight: 600; }
            </style>
        </head><body>${contentRef.current.outerHTML}
        <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
        </body></html>`;
        const popup = window.open('', '_blank', 'width=950,height=750,scrollbars=yes');
        if (popup) { popup.document.write(html); popup.document.close(); }
        else alert('Please allow popups for this site to print.');
    };

    return (
        <div ref={contentRef} className="print-container bg-white w-full min-h-screen p-8 text-gray-900 absolute top-0 left-0 z-50">
            {/* Header / Actions - Hidden on actual print */}
            <div className="flex justify-between items-center mb-8 border-b pb-4 no-print">
                <button
                    onClick={onClose}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition font-bold uppercase text-sm"
                >
                    <span>←</span> {t('Back to Application')}
                </button>
                <div className="flex items-center gap-3">
                    <div className="flex items-center bg-gray-100 border border-gray-300 rounded-lg p-0.5 shadow-sm">
                        <button
                            type="button"
                            onClick={() => i18n.changeLanguage('fr')}
                            className={`px-3 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                                (i18n.language || 'en').startsWith('fr')
                                    ? 'bg-amber-500 text-white shadow-xs'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            <span>🇫🇷</span> FR
                        </button>
                        <button
                            type="button"
                            onClick={() => i18n.changeLanguage('en')}
                            className={`px-3 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                                (i18n.language || 'en').startsWith('en')
                                    ? 'bg-indigo-600 text-white shadow-xs'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            <span>🇬🇧</span> EN
                        </button>
                    </div>
                    <button
                        onClick={handlePrint}
                        className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold uppercase shadow-md flex items-center gap-2 hover:bg-indigo-700 transition cursor-pointer"
                    >
                        🖨️ {t('Print Report')}
                    </button>
                </div>
            </div>

            {/* Document Header */}
            <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                    <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900">{t('Stock Transfer Report')}</h1>
                    <p className="text-gray-500 font-bold mt-1 text-sm">{t('Generated From ERP System')}</p>
                </div>
                <div className="text-right">
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest">{t('Date Range')}</p>
                    <p className="text-lg font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded inline-block mt-1">{dateRangeText}</p>
                </div>
            </div>

            {/* Main Table */}
            <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left border-collapse mt-4">
                <thead>
                    <tr className="border-b-2 border-gray-800 text-sm">
                        <th className="py-3 px-2 font-black uppercase text-gray-600 w-16">{t('ID')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600">{t('Product Name')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600">{t('Source (From)')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600">{t('Destination (To)')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600 text-right">{t('Quantity')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600">{t('Initiated By')}</th>
                        <th className="py-3 px-2 font-black uppercase text-gray-600 text-right">{t('Date / Time')}</th>
                    </tr>
                </thead>
                <tbody className="text-sm">
                    {transfers.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="py-8 text-center text-gray-400 italic font-bold">
                                {t('No transfers found for this date range.')}
                            </td>
                        </tr>
                    ) : (
                        transfers.map((tItem, index) => (
                            <tr key={tItem.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}`}>
                                <td className="py-3 px-2 font-mono text-xs text-gray-500">{tItem.id}</td>
                                <td className="py-3 px-2 font-bold text-gray-900">{tItem.product_name}</td>
                                <td className="py-3 px-2 text-orange-700 font-semibold">{tItem.from_depo_name}</td>
                                <td className="py-3 px-2 text-emerald-700 font-semibold">{tItem.to_depo_name}</td>
                                <td className="py-3 px-2 font-black text-indigo-700 text-right text-base">{tItem.quantity}</td>
                                <td className="py-3 px-2 text-gray-600">{tItem.user_name || 'System'}</td>
                                <td className="py-3 px-2 text-right text-gray-500 font-mono text-xs">
                                    {new Date(tItem.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
</div>

            {/* Footer Summary */}
            {transfers.length > 0 && (
                <div className="flex justify-end mt-4 pt-4 border-t-2 border-gray-800">
                    <div className="w-64">
                        <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <span className="font-bold text-sm uppercase text-gray-500">{t('Total Units Moved')}</span>
                            <span className="font-black text-xl text-gray-900">{totalQuantity.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Document Footer */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex justify-between items-center text-xs text-gray-400 font-mono">
                <p>{t('Report Generated:')} {new Date().toLocaleString()}</p>
                <p>{t('Generated By:')} {user?.username || 'Admin'}</p>
                <p>{t('Page')} 1 / 1</p>
            </div>

            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        .print-container, .print-container * { visibility: visible; }
                        .print-container { 
                            position: absolute; 
                            left: 0; 
                            top: 0; 
                            width: 100%; 
                            padding: 0;
                            margin: 0;
                            box-shadow: none;
                        }
                        .no-print { display: none !important; }
                        @page { size: auto; margin: 10mm; }
                    }
                `}
            </style>
        </div>
    );
};

export default TransferReportPrint;
